import { useEffect, useState } from "react"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination"
import { Search, Shield, CheckCircle, XCircle, Clock, AlertCircle } from "lucide-react"
import axios from "@/utils/axios"
import Loader from "@/components/loader/loader"
import { toast } from "sonner"
import { useAdminAuth } from "@/context/Admin"

export default function AllAdminsLoginHistory() {
  const { admin } = useAdminAuth()
  const isSuperAdmin = admin?.role === "super_admin"
  
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0,
    hasMore: false
  })
  const [emailFilter, setEmailFilter] = useState("")

  const fetchLoginHistory = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = {
        limit: pagination.limit,
        offset: pagination.offset,
      }

      if (emailFilter.trim()) {
        params.email = emailFilter.trim()
      }

      const res = await axios.get("/admin/all-login-history", { params })
      
      if (res.data) {
        setHistory(res.data.history || [])
        setPagination(prev => ({
          ...prev,
          total: res.data.pagination?.total || 0,
          hasMore: res.data.pagination?.hasMore || false,
        }))
      }
    } catch (err) {
      const message = err?.response?.data?.message || "Failed to fetch login history"
      setError(message)
      toast.error(message)
      
      // If access denied, redirect or show appropriate message
      if (err?.response?.status === 403) {
        setError("Access denied. Super admin role required.")
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (isSuperAdmin) {
      fetchLoginHistory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.limit, pagination.offset, isSuperAdmin])

  // Debounce email search
  useEffect(() => {
    if (!isSuperAdmin) return
    
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, offset: 0 }))
      fetchLoginHistory()
    }, 500)

    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailFilter])

  const handlePageChange = (direction) => {
    if (direction === "next" && pagination.hasMore) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))
    } else if (direction === "prev" && pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))
    }
  }

  const handleEmailFilterChange = (value) => {
    setEmailFilter(value)
  }

  const getSuccessBadge = (success) => {
    if (success) {
      return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <CheckCircle className="w-3 h-3" />
          Success
        </span>
      )
    }
    
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <XCircle className="w-3 h-3" />
        Failed
      </span>
    )
  }

  const formatDate = (dateString) => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    })
  }

  const getAuthMethodBadge = (method) => {
    const methodLower = method?.toLowerCase() || ""
    
    if (methodLower === "password") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
          Password
        </span>
      )
    }
    
    if (methodLower === "google" || methodLower === "oauth") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-700">
          Google OAuth
        </span>
      )
    }
    
    return (
      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700">
        {method || "N/A"}
      </span>
    )
  }

  const truncateUserAgent = (userAgent) => {
    if (!userAgent) return "N/A"
    if (userAgent.length > 50) {
      return userAgent.substring(0, 50) + "..."
    }
    return userAgent
  }

  // Restrict access to super_admin only
  if (!isSuperAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4 px-2 sm:px-4 lg:px-8">
        <div className="text-center max-w-md">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-600" />
          <h1 className="text-2xl sm:text-3xl font-bold text-red-600 mb-2">
            Access Denied
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            You do not have permission to access this page. Only super administrators can view all admins login history.
          </p>
        </div>
      </div>
    )
  }

  if (loading && history.length === 0) {
    return <Loader />
  }

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1
  const totalPages = Math.ceil(pagination.total / pagination.limit)

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Shield className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold text-primary">All Admins Login History</h1>
            <p className="text-gray-600 mt-1">View login history for all admin accounts across the platform.</p>
          </div>
        </div>
      </div>

      {/* Search Filter */}
      <div className="flex items-center gap-4 flex-wrap">
        <div className="relative flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
            placeholder="Filter by admin email..."
            className="pl-9 pr-4 py-2 rounded-md border border-input focus:ring-primary focus:border-primary"
            value={emailFilter}
            onChange={(e) => handleEmailFilterChange(e.target.value)}
          />
        </div>
      </div>

      {/* Login History Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-primary">Login History</CardTitle>
              <CardDescription>
                Total: {pagination.total} login attempts
                {emailFilter && ` (filtered by: ${emailFilter})`}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error && history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <Shield className="w-16 h-16 mb-4" />
              <p className="text-lg">{error}</p>
              <Button onClick={fetchLoginHistory} className="mt-4">
                Retry
              </Button>
            </div>
          ) : history.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>IP Address</TableHead>
                      <TableHead>User Agent</TableHead>
                      <TableHead>Auth Method</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date & Time</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="font-medium">
                          {entry.email || "N/A"}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {entry.ip_address || "N/A"}
                        </TableCell>
                        <TableCell className="text-sm max-w-[200px]" title={entry.user_agent || ""}>
                          {truncateUserAgent(entry.user_agent)}
                        </TableCell>
                        <TableCell>
                          {getAuthMethodBadge(entry.auth_method)}
                        </TableCell>
                        <TableCell>
                          {getSuccessBadge(entry.success)}
                        </TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(entry.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.total > pagination.limit && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} entries
                  </div>
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            handlePageChange("prev")
                          }}
                          className={pagination.offset <= 0 ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                      <PaginationItem>
                        <span className="px-4 py-2 text-sm">
                          Page {currentPage} of {totalPages}
                        </span>
                      </PaginationItem>
                      <PaginationItem>
                        <PaginationNext
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            handlePageChange("next")
                          }}
                          className={!pagination.hasMore ? "pointer-events-none opacity-50" : ""}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <Shield className="w-16 h-16 mx-auto mb-4" />
              <p className="text-lg">No login history found.</p>
              {emailFilter ? (
                <p className="text-sm mt-2">Try adjusting your email filter.</p>
              ) : null}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

